import { useCallback, useMemo } from 'react';

import { useBFFContext } from '../context';

export interface UseGenericCacheProps extends Record<string, any> {
	accountId?: string;
	cloudId?: string;
	excludeAtis?: string[];
	locale?: string;
	nativeAtis?: string[];
	orgId?: string;
	preferredAtis?: string[];
	productType: string;
	subProduct?: string;
}

/**
 * Helper function to create a deterministic cache key from an object
 * by sorting keys and stringifying the result
 */
function createSortedCacheKey(unordered: Record<string, any>): string {
	return JSON.stringify(
		Object.keys(unordered)
			.sort()
			.reduce(
				(obj, key) => {
					obj[key] = unordered[key];
					return obj;
				},
				{} as Record<string, any>,
			),
	);
}

/**
 * Generic cache hook that can work with any type T
 * @template T - The type of objects to be cached
 * @returns Object containing cache operations and current cached object
 */
export function useGenericCache<T>(props: UseGenericCacheProps) {
	const {
		value: { consumer, cache },
		setValue,
	} = useBFFContext();

	if (!consumer) {
		throw new Error('BFFContext Consumer is not set');
	}

	// Compute cache key using useMemo with sorted JSON stringification
	const cacheKey = useMemo(() => {
		const sortedJsonString = createSortedCacheKey(props);
		return `gc-${consumer}-${sortedJsonString}`;
	}, [consumer, props]);

	const cachedObject: T | undefined = cache[cacheKey];

	const setCachedObject = useCallback(
		(objectToCache: T) => {
			setValue((prev) => ({
				...prev,
				cache: {
					...prev.cache,
					[cacheKey]: objectToCache,
				},
			}));
		},
		[cacheKey, setValue],
	);

	const removeCachedObject = useCallback(() => {
		setValue((prev) => {
			const { [cacheKey]: removed, ...remainingCache } = prev.cache;
			return {
				...prev,
				cache: remainingCache,
			};
		});
	}, [cacheKey, setValue]);

	const getCachedObject = (targetCacheKey: string): T | undefined => cache[targetCacheKey];

	const hasCachedObject = (targetCacheKey: string): boolean => cache[targetCacheKey] !== undefined;

	return {
		setCachedObject,
		removeCachedObject,
		getCachedObject,
		hasCachedObject,
		cacheKey,
		cachedObject,
	};
}
